import { createContext, useContext, useState, ReactNode } from 'react';

type CallMode = 'job' | 'lead' | 'new-lead' | null;

interface CallMonitorContextType {
  activeJobId: string | null;
  activeLeadId: string | null;
  callMode: CallMode;
  setContext: (context: {
    activeJobId?: string | null;
    activeLeadId?: string | null;
    callMode: CallMode;
  }) => void;
  clearContext: () => void;
}

const CallMonitorContext = createContext<CallMonitorContextType | undefined>(undefined);

export function CallMonitorProvider({ children }: { children: ReactNode }) {
  const [activeJobId, setActiveJobId] = useState<string | null>(null);
  const [activeLeadId, setActiveLeadId] = useState<string | null>(null);
  const [callMode, setCallMode] = useState<CallMode>(null);

  const setContext = (context: {
    activeJobId?: string | null;
    activeLeadId?: string | null;
    callMode: CallMode;
  }) => {
    setActiveJobId(context.activeJobId ?? null);
    setActiveLeadId(context.activeLeadId ?? null);
    setCallMode(context.callMode);
  };

  const clearContext = () => {
    setActiveJobId(null);
    setActiveLeadId(null);
    setCallMode(null);
  };

  return (
    <CallMonitorContext.Provider
      value={{
        activeJobId,
        activeLeadId,
        callMode,
        setContext,
        clearContext,
      }}
    >
      {children}
    </CallMonitorContext.Provider>
  );
}

export function useCallMonitorContext() {
  const context = useContext(CallMonitorContext);
  if (context === undefined) {
    throw new Error('useCallMonitorContext must be used within a CallMonitorProvider');
  }
  return context;
}
